import { trackEvent } from '../../services/analytics';
import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Rating,
  CircularProgress,
  Alert,
} from '@mui/material';
import Header from '../layout/Header';
import { useNavigate, useParams } from 'react-router-dom';
import { getFeedbackFormData, submitFeedback } from '../../services/firebaseData';

export default function FeedbackPage() {
  const navigate = useNavigate();
  const { group_id } = useParams();
  const userId = localStorage.getItem('user_id');

  const [feedbackData, setFeedbackData] = useState({});
  const [feedback, setFeedback] = useState({
    venueFeedback: 5, // Default 5 stars
    ownerFeedback: 5, // Default 5 stars
    userFeedbacks: {},
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [state, setState] = useState('to_do'); // to_do, done, not_possible

  useEffect(() => {
    if (!userId) {
      navigate('/');
    }
  }, [navigate, userId]);

  const fetchFeedback = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError('');
    try {
      const data = await getFeedbackFormData(group_id, userId);
      setFeedbackData(data);
      setState(data.state);
      const saved = data.existingFeedbacks || [];
      setFeedback({
        venueFeedback: saved.find((item) => item.targetType === 'VENUE')?.value ?? 5,
        ownerFeedback: saved.find((item) => item.targetUserId === data.owner?.userId)?.value ?? 5,
        userFeedbacks: Object.fromEntries((data.users || []).map((user) => [
          user.userId, saved.find((item) => item.targetUserId === user.userId)?.value ?? 5,
        ])),
      });
    } catch (error) {
      console.error('Error fetching feedback:', error);
      setError('Impossibile caricare i feedback. Riprova.');
    } finally {
      setLoading(false);
    }
  }, [group_id, userId]);

  useEffect(() => {
    fetchFeedback();
  }, [fetchFeedback]);

  const handleFeedbackChange = (field, value) => {
    if (value === null) return;
    setFeedback((current) => ({ ...current, [field]: value }));
  };

  const handleSubmitFeedback = async () => {
    trackEvent('action_start', { action: 'submit_feedback' });
    setLoading(true);
    setError('');

    try {
      await submitFeedback(group_id, userId, feedback);
      trackEvent('feedback_submitted');
      await fetchFeedback();
    } catch (error) {
      console.error('Error submitting feedback:', error);
      setError(error.message || 'Invio non riuscito. Riprova.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 2, mx: 'auto', maxWidth: 720, mt: 'var(--cv-top-bar-height)', pb: 'calc(32px + var(--cv-safe-area-bottom))' }}>
      <Header title="Pagina Feedback" />
      {error && (
        <Alert severity="error" action={<Button color="inherit" onClick={fetchFeedback}>Riprova</Button>}>
          {error}
        </Alert>
      )}
      {loading ? (
        <CircularProgress />
      ) : !feedbackData.group && !error ? (
        <Typography variant="body1" color="textSecondary">
          Feedback non disponibile per questo tavolo.
        </Typography>
      ) : feedbackData.group ? (
        <Box sx={{ mt: 3 }}>
          {state === 'done' && <Alert severity="success">Feedback inviati. Puoi consultarli nel profilo.</Alert>}
          {state === 'not_possible' && <Alert severity="info">Puoi lasciare feedback solo dopo la fine di un tavolo a cui hai partecipato.</Alert>}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 'bold' }}
              color="primary"
            >
              {feedbackData.group.name}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {feedbackData.group.location?.place}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Quando:{' '}
              <strong>
                {new Date(feedbackData.group.startAt).toLocaleDateString(
                  'it-IT',
                  { day: '2-digit', month: '2-digit', year: 'numeric' },
                )}
              </strong>
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Interessi: <strong>{feedbackData.group.interests?.join(', ')}</strong>
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Orario:{' '}
              <strong>
                {new Date(feedbackData.group.startAt).toLocaleTimeString(
                  [],
                  { hour: '2-digit', minute: '2-digit' },
                )}{' '}
                -{' '}
                {new Date(feedbackData.group.endAt).toLocaleTimeString(
                  [],
                  { hour: '2-digit', minute: '2-digit' },
                )}
              </strong>
            </Typography>
            {feedbackData.group.note && (
              <Typography variant="body2" color="textSecondary">
                Note: <strong>{feedbackData.group.note}</strong>
              </Typography>
            )}

            {/* Venue Feedback */}
            {feedbackData?.venue && (
              <Typography variant="h6" sx={{ flex: 1, mt: 2, mb: 0 }}>
                Ristorante
              </Typography>
            )}
            {feedbackData?.venue && (
              <Box sx={{ mt: 1, display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2" sx={{ flex: 1 }}>
                  {feedbackData.venue.name}
                </Typography>
                <Rating
                  name="venue-feedback"
                  value={feedback.venueFeedback}
                  onChange={(event, newValue) =>
                    handleFeedbackChange('venueFeedback', newValue)
                  }
                  sx={{ color: 'gold' }}
                  size="large"
                  readOnly={state !== 'to_do'}
                />
              </Box>
            )}

            {/* Owner Feedback */}
            {feedbackData?.owner && (
              <Typography variant="h6" sx={{ flex: 1, mt: 2, mb: 0 }}>
                Organizzatore
              </Typography>
            )}
            {feedbackData?.owner && (
              <Box sx={{ mt: 1, display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2" sx={{ flex: 1 }}>
                  {feedbackData.owner.displayName || 'Organizzatore'}
                </Typography>
                <Rating
                  name="owner-feedback"
                  value={feedback.ownerFeedback}
                  onChange={(event, newValue) =>
                    handleFeedbackChange('ownerFeedback', newValue)
                  }
                  sx={{ color: 'gold' }}
                  size="large"
                  readOnly={state !== 'to_do'}
                />
              </Box>
            )}

            {/* User Feedback */}
            {feedbackData?.users && feedbackData.users.length > 0 && (
              <Typography variant="h6" sx={{ flex: 1, mt: 2, mb: 0 }}>
                Convivials
              </Typography>
            )}
            {feedbackData?.users &&
              feedbackData.users.map((user) => (
                <Box
                  key={user.userId}
                  role="group"
                  aria-label={`Feedback per ${user.displayName || 'Commensale'}`}
                  sx={{ mt: 1, display: 'flex', alignItems: 'center' }}
                >
                  <Typography variant="body2" sx={{ flex: 1 }}>
                    {user.displayName || 'Commensale'}
                  </Typography>
                  <Rating
                    name={`user-feedback-${user.userId}`}
                    value={feedback.userFeedbacks[user.userId] || 5}
                    onChange={(event, newValue) =>
                      handleFeedbackChange('userFeedbacks', {
                        ...feedback.userFeedbacks,
                        [user.userId]: newValue,
                      })
                    }
                    sx={{ color: 'gold' }}
                    size="large"
                    readOnly={state !== 'to_do'}
                  />
                </Box>
              ))}

            {/* Submit Button */}
            {state === 'to_do' && (
              <Button
                sx={{ mt: 4 }}
                variant="contained"
                color="primary"
                onClick={handleSubmitFeedback}
              >
                Conferma Feedback
              </Button>
            )}
          </Box>
        </Box>
      ) : null}
    </Box>
  );
}
